import { z } from 'zod';
import {
  IS_POSITIVE,
  IS_REQUIRED,
  MINIMUM_ITEMS_ERROR_MESSAGE_TEXT,
} from '@occc-app/lib/constants/validationMessages';
import { LocationFragment, LocationGroupFragment } from '@occc/graphql';
import { TransactionsMemberPurchasedOperator } from './constants';

import {
  fixedMonthSchema,
  fixedWindowSchema,
  timeWindowSchema,
} from '../../validation/time-period.validation';
import { TransactionTimePeriod } from '../../types';

const validateThresholdAndProductGroups = (
  data: z.infer<typeof purchasesSchema>[number],
  ctx: z.RefinementCtx,
  pathPrefix: (string | number)[] = [],
) => {
  const { productGroups, threshold, locations, timePeriodType } = data;
  const hasThreshold = typeof threshold === 'number';
  const hasProductGroups = !!productGroups?.length;
  const hasLocations = !!locations?.length;
  const hasLocationGroups = !!data.locationGroups?.length;

  const hasRollingWindow =
    timePeriodType === TransactionTimePeriod.RollingWindow && data.timeWindow;
  const hasFixedMonth = timePeriodType === TransactionTimePeriod.FixedMonth && data.fixedMonth;
  const hasFixedWindow =
    timePeriodType === TransactionTimePeriod.FixedWindow &&
    data.fixedWindow?.startDate &&
    data.fixedWindow?.endDate;

  if (hasThreshold && threshold <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: IS_POSITIVE,
      path: [...pathPrefix, 'threshold'],
    });
  }

  if ((hasProductGroups || hasLocations || hasLocationGroups) && !hasThreshold) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: IS_REQUIRED,
      path: [...pathPrefix, 'threshold'],
    });
  }

  if (
    (hasThreshold ||
      hasLocations ||
      hasLocationGroups ||
      hasRollingWindow ||
      hasFixedMonth ||
      hasFixedWindow) &&
    !hasProductGroups
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: MINIMUM_ITEMS_ERROR_MESSAGE_TEXT,
      path: [...pathPrefix, 'productGroups'],
    });
  }
};

const purchasesSchemaSchemaCommon = z
  .object({
    productGroups: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
    threshold: z.number().nullable().optional(),
    timePeriodType: z.nativeEnum(TransactionTimePeriod),
    locations: z.array(z.custom<LocationFragment>()).optional(),
    locationGroups: z.array(z.custom<LocationGroupFragment>()).optional(),
    type: z.nativeEnum(TransactionsMemberPurchasedOperator),
    includeThreshold: z.boolean().optional(),
    timeWindow: timeWindowSchema.optional(),
    fixedMonth: fixedMonthSchema.optional(),
    fixedWindow: fixedWindowSchema.optional(),
  })
  .superRefine((data, ctx) => {
    if (data.timePeriodType === TransactionTimePeriod.RollingWindow && !data.timeWindow) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: IS_REQUIRED,
        path: ['timeWindow'],
      });
    }

    if (data.timePeriodType === TransactionTimePeriod.FixedMonth && !data.fixedMonth) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: IS_REQUIRED,
        path: ['fixedMonth'],
      });
    }

    if (data.timePeriodType === TransactionTimePeriod.FixedWindow && !data.fixedWindow?.startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: IS_REQUIRED,
        path: ['fixedWindow', 'startDate'],
      });
    }

    if (data.timePeriodType === TransactionTimePeriod.FixedWindow && !data.fixedWindow?.endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: IS_REQUIRED,
        path: ['fixedWindow', 'endDate'],
      });
    }

    validateThresholdAndProductGroups(data, ctx);
  });

export const purchasesSchema = z
  .array(purchasesSchemaSchemaCommon)
  .min(1, 'At least one purchase item is required');
